export const EVENT_PAGE_SIZE = 50;
export const EVENT_FILTER_KEYS = [
  "camera",
  "direction",
  "vehicle",
  "status",
  "kind",
  "from",
  "to",
  "sort",
];

type Event = {
  id: string;
  camera_id: string;
  kind: string;
  direction: string;
  class_label: string;
  source_seconds?: number;
  interval_start: number;
  decision?: { verdict: string };
};
type Camera = { id: string; direction_labels?: Record<string, string> };

export const eventSeconds = (event: Event) =>
  event.source_seconds ?? event.interval_start;
export const eventDirection = (event: Event, cameras: Camera[]) =>
  cameras.find((camera) => camera.id === event.camera_id)?.direction_labels?.[
    event.direction
  ] || event.direction;

// Elapsed times are relative to each recording, never camera/LPR wall-clock time.
export function selectEvents<T extends Event>(
  all: T[],
  cameras: Camera[],
  params: URLSearchParams,
  reviewQueue: boolean,
) {
  const from = params.get("from") || "";
  const to = params.get("to") || "";
  const start = from === "" ? 0 : Number(from) * 60;
  const end = to === "" ? Infinity : Number(to) * 60;
  const error =
    !Number.isFinite(start) ||
    start < 0 ||
    (to !== "" && !Number.isFinite(end)) ||
    end < 0
      ? "Use a non-negative number of minutes."
      : start > end
        ? "End time must be at or after start time."
        : "";
  const base = all.filter(
    (event) =>
      !reviewQueue ||
      (event.kind === "tracking_gap" &&
        (!event.decision || event.decision.verdict === "unresolved")),
  );
  const events = error
    ? []
    : base
        .filter((event) => {
          const values: Record<string, string> = {
            camera: event.camera_id,
            direction: eventDirection(event, cameras),
            vehicle: event.class_label,
            status: event.decision?.verdict || "unreviewed",
            kind: event.kind,
          };
          return (
            Object.entries(values).every(
              ([key, value]) => !params.get(key) || params.get(key) === value,
            ) &&
            eventSeconds(event) >= start &&
            eventSeconds(event) <= end
          );
        })
        .sort((a, b) => {
          const delta = eventSeconds(a) - eventSeconds(b);
          return (
            (params.get("sort") === "latest" ? -delta : delta) ||
            a.camera_id.localeCompare(b.camera_id) ||
            a.id.localeCompare(b.id)
          );
        });
  const requestedIndex = events.findIndex(
    (event) => event.id === params.get("event"),
  );
  const pages = Math.max(1, Math.ceil(events.length / EVENT_PAGE_SIZE));
  const inputPage = Number(params.get("page") || 1);
  const page =
    requestedIndex >= 0
      ? Math.floor(requestedIndex / EVENT_PAGE_SIZE) + 1
      : Math.min(
          pages,
          Math.max(1, Number.isFinite(inputPage) ? Math.floor(inputPage) : 1),
        );
  const offset = (page - 1) * EVENT_PAGE_SIZE;
  const selectedIndex =
    requestedIndex >= 0 ? requestedIndex : events.length ? offset : -1;
  return {
    events,
    total: base.length,
    error,
    page,
    pages,
    offset,
    rows: events.slice(offset, offset + EVENT_PAGE_SIZE),
    selectedIndex,
    selected: events[selectedIndex],
  };
}
